import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FiMessageSquare, FiSearch, FiPhone, FiFileText, FiBarChart2,
  FiZap, FiLock, FiHash, FiMapPin, FiSettings, FiPaperclip,
} from 'react-icons/fi';
import { RiSparklingFill } from 'react-icons/ri';
import ChatMessage from '../chat/ChatMessage';
import ChatInput from '../chat/ChatInput';
import { useAmpsLoaded } from '../../context/AmpContext';
import { useAuth } from '../auth/AuthContext';
import { useStatusTemplate } from '../../context/StatusTemplateContext';
import AmsRequiredPage from '../ams/AmsRequiredPage';
import RateQuoteLinkPage from '../ratequotelink/RateQuoteLinkPage';
import CarrierAccessPage from '../carrieraccess/CarrierAccessPage';
import * as chatService from '../../services/chatService';
import * as historyService from '../../services/historyService';
import ArchiveChatButton from '../../components/ui/ArchiveChatButton';
import { buildAmpPrompts } from '../chat/ampPrompts';
import { useAutoScroll } from '../../hooks/useAutoScroll';
import { useTypewriter } from '../../hooks/useTypewriter';
import type { ChatMessage as ChatMessageType, ChatThread, FileAttachment, TableData } from '../chat/chat.types';
import FilePreviewModal from '../../components/ui/FilePreviewModal';
import '../../styles/AmpChat.css';

const AMP_ICON_MAP: Record<string, React.ReactNode> = {
  message: <FiMessageSquare size={28} />,
  search: <FiSearch size={28} />,
  phone: <FiPhone size={28} />,
  file: <FiFileText size={28} />,
  image: <RiSparklingFill size={28} />,
  scan: <FiSearch size={28} />,
  dollar: <FiBarChart2 size={28} />,
  chart: <FiBarChart2 size={28} />,
  shield: <FiLock size={28} />,
  database: <FiHash size={28} />,
  car: <FiMapPin size={28} />,
  compare: <FiBarChart2 size={28} />,
  zap: <FiZap size={28} />,
};

function parseMarkdownTable(content: string): import('../chat/chat.types').TableData | null {
  const lines = content.split('\n');
  const tableLines: string[] = [];
  let capturing = false;

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith('|')) {
      capturing = true;
      tableLines.push(trimmed);
    } else if (capturing) {
      break;
    }
  }

  if (tableLines.length < 2) return null;

  // Remove separator rows like |---|---|
  const dataLines = tableLines.filter(l => !/^\|[\s:|-]+\|$/.test(l));
  if (dataLines.length < 2) return null;

  const parseRow = (line: string) =>
    line.split('|').slice(1, -1).map(c => c.trim());

  return {
    headers: parseRow(dataLines[0]),
    rows: dataLines.slice(1).map(parseRow),
  };
}

function extractFilesFromMessageContent(content: string): FileAttachment[] {
  const rows: FileAttachment[] = [];
  const lines = (content || '').split('\n');
  let inAttachedSection = false;

  for (const raw of lines) {
    const line = raw.trim();
    if (!line) {
      if (inAttachedSection) break;
      continue;
    }
    if (/^\[?Attached files\]?\s*:?$/i.test(line)) {
      inAttachedSection = true;
      continue;
    }
    if (!inAttachedSection) continue;

    const bullet = line.replace(/^[-*]\s*/, '').trim();
    const withUrl = bullet.match(/^(.+?)\s*:\s*(https?:\/\/\S.*)$/i);
    if (withUrl) {
      rows.push({ name: withUrl[1].trim(), fileUrl: withUrl[2].trim(), size: 0, type: '' });
      continue;
    }

    const urlOnly = bullet.match(/^(https?:\/\/\S.*)$/i);
    if (!urlOnly) continue;

    const url = urlOnly[1].trim();
    const rawName = url.split('?')[0].split('/').pop() || 'Attached file';
    rows.push({ name: decodeURIComponent(rawName), fileUrl: url, size: 0, type: '' });
  }

  return rows;
}

function generateId(): string {
  if (crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface AmpChatContainerProps {
  onCommissionScan?: (pdf: FileAttachment, table: import('../chat/chat.types').TableData, conversationId: string) => void;
  onResetScan?: () => void;
  ampIdOverride?: string;
  basePath?: string;
}

const AmpChatContainer: React.FC<AmpChatContainerProps> = ({ onCommissionScan, onResetScan, ampIdOverride, basePath }) => {
  const { ampId: routeAmpId, conversationId: routeConversationId } = useParams<{ ampId: string; conversationId?: string }>();
  const ampId = ampIdOverride || routeAmpId;
  const ampBasePath = basePath || `/amp/${ampId}`;
  const navigate = useNavigate();
  const location = useLocation();
  const { getAmp, getAmpMessages, addMessage, initialLoading } = useAmpsLoaded();
  const { isAMSUser } = useAuth();

  // Support navigation state with a pre-filled prompt (e.g. from dashboard CTA buttons)
  const navPrompt = (location.state as { prompt?: string } | null)?.prompt || '';

  const [conversationId, setConversationId] = useState<string | undefined>(routeConversationId);
  const [messages, setMessages] = useState<ChatMessageType[]>([]);
  const [isThinking, setIsThinking] = useState(false);
  const [thoughtText, setThoughtText] = useState('');
  const [currentTool, setCurrentTool] = useState<string | undefined>();
  const [streamingContent, setStreamingContent] = useState('');
  const [pendingPrompt, setPendingPrompt] = useState(navPrompt);
  const [recentHistory, setRecentHistory] = useState<ChatThread[]>([]);

  // An archived chat leaves "Recent Conversations" right away.
  useEffect(() => {
    const onArchived = (e: Event) => {
      const id = (e as CustomEvent<{ conversationId: string }>).detail?.conversationId;
      if (id) setRecentHistory((prev) => prev.filter((t) => t.id !== id));
    };
    window.addEventListener(historyService.CHAT_ARCHIVED_EVENT, onArchived);
    return () => window.removeEventListener(historyService.CHAT_ARCHIVED_EVENT, onArchived);
  }, []);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [scanPdfAttachment, setScanPdfAttachment] = useState<FileAttachment | null>(null);
  const [scanTable, setScanTable] = useState<TableData | null>(null);
  const [previewFile, setPreviewFile] = useState<{ name: string; url: string } | null>(null);
  const displayedContent = useTypewriter(streamingContent, streamingContent.length > 0);
  const scrollRef = useAutoScroll(messages, displayedContent || thoughtText);
  const activeConvRef = useRef<string | undefined>(conversationId);
  const prevRouteIdRef = useRef<string | undefined>(routeConversationId);
  const preloadedHistoryRef = useRef<Map<string, ChatMessageType[]>>(new Map());

  const { setStatusTemplate } = useStatusTemplate();
  const amp = ampId ? getAmp(ampId) : null;
  const ampPrompts = amp ? buildAmpPrompts(amp) : undefined;
  const topAttachments = React.useMemo(() => {
    const seen = new Set<string>();
    const rows: FileAttachment[] = [];
    for (const msg of messages) {
      const attachments = [...(msg.metadata?.attachments ?? []), ...extractFilesFromMessageContent(msg.content)];
      for (const att of attachments) {
        const key = (att.fileUrl || att.preview || att.name || '').trim().toLowerCase();
        if (!key || seen.has(key)) continue;
        seen.add(key);
        rows.push(att);
      }
    }
    return rows;
  }, [messages]);

  // Fetch rate token when the "Rate Submission" AMP is opened
  useEffect(() => {
    if (initialLoading || !ampId) return;
    const currentAmp = getAmp(ampId);
    if (!currentAmp || !['Rate & Submission Builder', 'Leads & Prospects', 'Renewals'].includes(currentAmp.name)) return;

    const amsToken = localStorage.getItem('amsToken');
    if (!amsToken) return;
    fetch(`${(process.env.REACT_APP_Auth_Url)}/api/account/ratetoken?nctoken=${encodeURIComponent(amsToken)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (!data) return;
        
        if (data.igToken) {
          localStorage.setItem('rateToken', data.igToken);
        }
      })
      .catch(err => console.error('Failed to fetch rate token:', err));
  }, [ampId, initialLoading]);

  const AMS_REQUIRED_AMPS = new Set([
    'AMS Copilot',
    'Certificates',
    'Data Import',
    'Leads & Prospects',
    'Quote Proposal Generator',
    'Renewals',
    'Reports',
  ]);

  // Handle route changes and new chat
  useEffect(() => {
    const prevRouteId = prevRouteIdRef.current;
    prevRouteIdRef.current = routeConversationId;

    // Case 1: No route ID (new chat clicked) - clear state and create new conversation
    if (!routeConversationId && ampId) {
      setMessages([]);
      setIsThinking(false);
      setThoughtText('');
      setCurrentTool(undefined);
      setStreamingContent('');
      setPendingPrompt('');
      setStatusTemplate(null);
      setScanPdfAttachment(null);
      setScanTable(null);
      onResetScan?.();
      const newConversationId = generateId();
      setConversationId(newConversationId);
      navigate(`${ampBasePath}/${newConversationId}`, { replace: true });
      return;
    }

    // Case 2: Route ID exists and changed - load messages for that conversation
    if (routeConversationId && routeConversationId !== prevRouteId) {
      setConversationId(routeConversationId);
      setStatusTemplate(null);
      const preloaded = preloadedHistoryRef.current.get(routeConversationId);
      if (preloaded && preloaded.length > 0) {
        preloadedHistoryRef.current.delete(routeConversationId);
        setMessages(preloaded);
      } else {
        const key = `${ampId}:${routeConversationId}`;
        const ampMsgs = getAmpMessages(key);
        const realMessages = ampMsgs.filter(m => !m.id.endsWith('-welcome'));
        setMessages(realMessages);
      }
      setIsThinking(false);
      setThoughtText('');
      setCurrentTool(undefined);
      setStreamingContent('');
    }
  }, [routeConversationId, ampId, navigate, getAmpMessages, setStatusTemplate, onResetScan]);

  useEffect(() => {
    activeConvRef.current = conversationId;
  }, [conversationId]);

  // Fetch recent history for this amp
  useEffect(() => {
    if (!ampId) { setRecentHistory([]); return; }
    let cancelled = false;
    setHistoryLoading(true);
    historyService.fetchRecentHistoryForAmp(undefined, ampId).then(({ threads }) => {
      if (!cancelled) { setRecentHistory(threads); setHistoryLoading(false); }
    });
    return () => { cancelled = true; };
  }, [ampId]);

  const handleHistoryThreadClick = useCallback(async (threadId: string) => {
    setConversationId(threadId);
    setMessages([]);
    setScanPdfAttachment(null);
    setScanTable(null);
    setHistoryLoading(true);
    const { messages: msgs, pdfUrls } = await historyService.fetchConversationDetail(threadId);
    setHistoryLoading(false);
    preloadedHistoryRef.current.set(threadId, msgs);
    setMessages(msgs);

    // Restore commission scan state from history
    if (amp?.name?.toLowerCase().includes('commission')) {
      const assistantMsgs = msgs.filter(m => m.role === 'assistant');
      for (const msg of assistantMsgs) {
        const parsed = parseMarkdownTable(msg.content);
        if (parsed) {
          setScanTable(parsed);
          setScanPdfAttachment({
            name: 'Commission File',
            size: 0,
            type: '',
            fileUrl: pdfUrls[0] || undefined,
          });
          break;
        }
      }
    }

    if (ampId) {
      navigate(`${ampBasePath}/${threadId}`, { replace: true });
    }
  }, [ampId, amp, navigate]);

  const handleSend = useCallback(async (content: string, attachments?: FileAttachment[], apiPrompt?: string) => {
    if (!ampId || !amp || !conversationId) return;

    // Only show non-silent attachments in the chat UI; silent ones are background API context
    const displayAttachments = attachments?.filter(a => !a.silent);
    const userMsg: ChatMessageType = {
      id: `user-${Date.now()}`,
      role: 'user',
      content,
      timestamp: new Date().toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      }),
      ...(displayAttachments && displayAttachments.length > 0 ? { metadata: { attachments: displayAttachments } } : {}),
    };

    setMessages(prev => [...prev, userMsg]);

    // Commission scan: capture any uploaded file from explicitly attached files (not silent refs)
    if (amp.name?.toLowerCase().includes('commission') && displayAttachments?.length) {
      setScanPdfAttachment(displayAttachments[0]);
      setScanTable(null);
    }

    const msgKey = `${ampId}:${conversationId}`;
    addMessage(msgKey, userMsg);
    setIsThinking(true);
    setThoughtText('');
    setCurrentTool(undefined);
    setStreamingContent('');

    try {
      // Extract file paths from attachments
      const filePaths = attachments
        ?.filter(att => !!att.fileUrl)
        .map(att => ({ fileName: att.name, fileURL: att.fileUrl! })) || [];

      const lastImageUrl = chatService.getLastImageUrl(messages);

      await chatService.sendMessage(apiPrompt ?? content, amp.chatUrl, {
        onThought: (thoughtSoFar, tool) => {
          setIsThinking(true);
          setThoughtText(thoughtSoFar);
          if (tool) setCurrentTool(tool);
        },
        onChunk: (textSoFar) => {
          setIsThinking(false);
          setStreamingContent(textSoFar);
        },
        onDone: (finalMsg) => {
          setStreamingContent('');
          setIsThinking(false);
          setThoughtText('');
          setCurrentTool(undefined);
          setMessages(prev => [...prev, finalMsg]);
          if (activeConvRef.current) {
            addMessage(`${ampId}:${activeConvRef.current}`, finalMsg);
          }
          // Commission Statements: parse markdown table from response for advanced panel
          if (amp.name === 'Commission Statements') {
            const parsed = parseMarkdownTable(finalMsg.content);
            if (parsed) setScanTable(parsed);
          }
        },
      }, conversationId, filePaths, ampId, undefined, lastImageUrl);
    } catch {
      setIsThinking(false);
      setStreamingContent('');
      setThoughtText('');
    }
  }, [ampId, amp, conversationId, addMessage, messages]);

  const handlePromptClick = useCallback((prompt: string) => {
    setPendingPrompt(prompt);
  }, []);

  if (initialLoading) {
    return (
      <div className="amp-chat">
        <div className="chat-loader">
          <div className="chat-loader__welcome">
            <div className="chat-loader__icon-skeleton" />
            <div className="chat-loader__line chat-loader__line--title" />
            <div className="chat-loader__line chat-loader__line--desc" />
            <div className="chat-loader__hints">
              <div className="chat-loader__hint-pill" />
              <div className="chat-loader__hint-pill" />
              <div className="chat-loader__hint-pill" />
            </div>
          </div>
          <div className="chat-loader__prompts">
            {[0, 1, 2, 3].map(i => (
              <div key={i} className="chat-loader__prompt-card" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (amp && AMS_REQUIRED_AMPS.has(amp.name) && !isAMSUser) {
    return <AmsRequiredPage featureName={amp.name} amp={amp} />;
  }

  if (!amp) {
    return (
      <div className="amp-not-found">
        <div className="amp-not-found__content glass-card">
          <FiZap size={48} />
          <h2>AMP Not Found</h2>
          <p>This module doesn't exist or has been removed.</p>
          <button
            className="glass-btn glass-btn-primary"
            onClick={() => navigate('/amps')}
          >
            Browse AMPs
          </button>
        </div>
      </div>
    );
  }

  const showWelcome = messages.length === 0 && !isThinking;
  const hasActiveStream = thoughtText || streamingContent;
  const isCommissionScanner = !!amp.name?.toLowerCase().includes('commission');
  const showAdvancedButton = isCommissionScanner && !!scanPdfAttachment && !isThinking && !streamingContent;
  return (
    <div className="amp-chat">
      {previewFile && (
        <FilePreviewModal
          name={previewFile.name}
          url={previewFile.url}
          onClose={() => setPreviewFile(null)}
        />
      )}
      <div className="amp-chat__container" ref={scrollRef}>
        <div className="amp-chat__messages">
          {showWelcome && (
            <motion.div
              className="amp-welcome"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
            >
              <div className="amp-welcome__icon-wrap">
                <div
                  className="amp-welcome__icon"
                  style={{ background: `linear-gradient(135deg, ${amp.color}28, ${amp.color}14)`, color: amp.color, border: `1px solid ${amp.color}22` }}
                >
                  {AMP_ICON_MAP[amp.icon] || <FiZap size={24} />}
                </div>
                <div className="amp-welcome__icon-glow" style={{ background: amp.color }} />
              </div>
              <h2 className="amp-welcome__title">{amp.name}</h2>
              <p className="amp-welcome__desc">{amp.description?.replace(/\$\$[A-Z_]+(?:START|END)\$\$[\s\S]*?\$\$[A-Z_]+END\$\$/g, '').replace(/\$\$[A-Z_]+(?:START|END)\$\$[\s\S]*/g, '').trim()}</p>
              <div className="amp-welcome__hints">
                <span className="amp-welcome__hint">Specialized AI for {amp.category.toLowerCase()} tasks</span>
                <span className="amp-welcome__hint">Ask questions or upload documents</span>
                <span className="amp-welcome__hint">All conversations are saved</span>
              </div>
            </motion.div>
          )}

          {showWelcome && ampPrompts && (
            <div className="channel-prompts">
              {ampPrompts.map((item, i) => (
                <motion.button
                  key={item.id}
                  className="channel-prompts__card glass-surface"
                  onClick={() => handlePromptClick(item.prompt)}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + i * 0.06, duration: 0.35 }}
                  whileHover={{ scale: 1.02, y: -2 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="channel-prompts__icon" style={{ color: item.color }}>
                    {item.icon}
                  </span>
                  <span className="channel-prompts__prompt">{item.prompt}</span>
                </motion.button>
              ))}
            </div>
          )}

          {messages.length === 0 && (historyLoading || recentHistory.length > 0) && (
            <motion.div
              className="channel-history"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.15 }}
            >
              <div className="channel-history__header">
                <FiMessageSquare size={13} />
                <span>Recent Conversations</span>
              </div>
              {historyLoading ? (
                <div className="channel-history__loading">Loading history...</div>
              ) : (
                <div className="channel-history__list">
                  {recentHistory.map((thread, i) => (
                    <motion.div
                      key={thread.id}
                      role="button"
                      tabIndex={0}
                      className="channel-history__item has-archive-chat"
                      onClick={() => handleHistoryThreadClick(thread.id)}
                      onKeyDown={(e) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHistoryThreadClick(thread.id); }
                      }}
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.04 }}
                      whileHover={{ x: 4 }}
                    >
                      <FiMessageSquare size={13} className="channel-history__item-icon" />
                      <span className="channel-history__item-title">{thread.title}</span>
                      <span className="channel-history__item-time">
                        {new Date(thread.updatedAt).toLocaleDateString()}
                      </span>
                      <ArchiveChatButton conversationId={thread.id} />
                    </motion.div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {topAttachments.length > 0 && (
            <>
            <div style={{ position: 'sticky', top: 'calc(-1 * var(--space-4))', left: 0, zIndex: 1200, background: 'var(--bg-primary)', padding: 'calc(var(--space-4) + 6px) var(--space-5) 10px var(--space-5)', marginLeft: 'calc(-1 * var(--space-5))', marginBottom: '10px', width: 'calc(100% + (2 * var(--space-5)))', borderBottom: '1px solid var(--border-glass)', boxShadow: '0 4px 10px -4px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', background: 'transparent', border: 'none', borderRadius: 0, padding: 0, boxShadow: 'none', maxWidth: 'min(92vw, 980px)' }}>
              <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.07em', flexShrink: 0 }}>
                Files
              </span>
              {topAttachments.map((file, idx) => {
                const fileLink = file.fileUrl || file.preview;
                const displayName = decodeURIComponent((file.name || '').replace(/\+/g, ' '))
                  .replace(/\s*,\s*/g, ', ')
                  .replace(/\s+/g, ' ')
                  .trim();
                const canOpen = !!fileLink;
                return (
                  <button
                    key={`amp-chat-top-file-${displayName}-${idx}`}
                    type="button"
                    disabled={!canOpen}
                    title={displayName}
                    onClick={() => {
                      if (!fileLink) return;
                      setPreviewFile({ name: displayName, url: fileLink });
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      border: '1px solid var(--border-glass)',
                      borderRadius: 999,
                      padding: '4px 10px',
                      background: 'rgba(139,92,246,0.08)',
                      fontSize: 11,
                      fontWeight: 600,
                      color: 'var(--text-primary)',
                      cursor: canOpen ? 'pointer' : 'default',
                      maxWidth: 260,
                      opacity: canOpen ? 1 : 0.65,
                    }}
                  >
                    <FiPaperclip size={11} />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
                  </button>
                );
              })}
            </div>
            </div>
            </>
          )}

          {messages.map((msg, idx) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              chatId={conversationId}
              onQuickReply={
                idx === messages.length - 1 && msg.role === 'assistant' && !hasActiveStream && !isThinking
                  ? handleSend
                  : undefined
              }
              // A carrier choice card (several policies) sends the person's pick as their next message.
              onChoose={!hasActiveStream && !isThinking ? (text: string) => handleSend(text) : undefined}
            />
          ))}

          {isThinking && !thoughtText && !streamingContent && (
            <motion.div
              className="chat-msg chat-msg--assistant chat-msg--thinking"
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="chat-msg__header">
                <div className="chat-msg__avatar">
                  <div className="chat-msg__avatar-ai">
                    <RiSparklingFill size={14} />
                  </div>
                </div>
                <span className="chat-msg__sender">{amp.name}</span>
              </div>
              <div className="chat-msg__body">
                <div className="thinking-indicator">
                  <div className="thinking-indicator__shimmer" />
                  <span className="thinking-indicator__text">
                    <RiSparklingFill size={12} className="thinking-indicator__icon" />
                    Thinking...
                  </span>
                </div>
              </div>
            </motion.div>
          )}

          {hasActiveStream && (
            <ChatMessage
              message={{
                id: 'streaming',
                role: 'assistant',
                content: displayedContent,
                isStreaming: true,
                timestamp: new Date().toLocaleTimeString([], {
                  hour: 'numeric',
                  minute: '2-digit',
                }),
                metadata: {
                  ...(thoughtText ? { thoughtText } : {}),
                  ...(currentTool ? { currentTool } : {}),
                },
              }}
            />
          )}
        </div>
      </div>

      {showAdvancedButton && (
        <div className="amp-advanced-bar">
          <motion.button
            className="amp-advanced-btn"
            onClick={() => onCommissionScan?.(scanPdfAttachment!, scanTable!, conversationId ?? '')}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.97 }}
          >
            <FiSettings size={13} />
            Advanced Options
          </motion.button>
        </div>
      )}

      <div className="amp-chat__input-area">
        <ChatInput
          onSend={handleSend}
          disabled={isThinking || streamingContent.length > 0}
          placeholder={`Message ${amp.name}...`}
          externalValue={pendingPrompt}
          conversationId={conversationId}
          selectedAmpId={ampId}
        />
      </div>
    </div>
  );
};

/**
 * Wrapper that intercepts the RateQuoteLink AMP at the route boundary and
 * renders the custom RateQuoteLinkPage instead of the generic chat UI.
 *
 * Doing this in a wrapper (rather than inside AmpChatContainer) is required by
 * the React Rules of Hooks — a conditional early return inside AmpChatContainer
 * would skip its hooks on the RateQuoteLink path, breaking hook order across
 * renders. The wrapper itself uses a single hook (useParams) so it complies.
 */
const AmpChatContainerRouter: React.FC<AmpChatContainerProps> = (props) => {
  const { ampId: routeAmpId } = useParams<{ ampId: string }>();
  const { getAmp } = useAmpsLoaded();
  const ampId = props.ampIdOverride || routeAmpId;
  const amp = ampId ? getAmp(ampId) : null;
  if (amp?.category === 'Rate Quoting Skill' || amp?.name === 'Rate Quoting Skill') {
    return <RateQuoteLinkPage />;
  }
  if (amp?.category === 'Carrier Access' || amp?.name === 'Carrier Access') {
    return <CarrierAccessPage />;
  }
  return <AmpChatContainer {...props} />;
};

export default AmpChatContainerRouter;

